import { Checkbox, Divider } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import Icon, { ICON_NAME } from "../../../components/Icon";
import LoadingBox from "../../../components/LoadingBox";
import { datetime } from "../../../util/library/datetime";
import { IResponseSummarySites, SiteService } from "../../../util/services/site";
import { selectOneSetting, selectSetting } from "../../../util/store/selectors";
import { useSelector } from "../../../util/store/store";
import { diseaseType } from "../../../util/varibles/constants";
import { formatNumber, removeProperty } from "../../../util/varibles/global";
import { SiteOverviewSetting } from "../../../util/varibles/userSetting";
import { IUser } from "../../LoginPage/constants";
import { updateUserSetting } from "../../LoginPage/reducer";
import { FILTER_ID } from "../Filter";
import { setSiteOverview, useSiteOverview } from "../SiteOverviewContext";
import FishHealth from "./FishHealth";
import SiteUtilize from "./SiteUtilize";
import styles from './style.module.scss';
import SummarySetting from "./SummarySetting";

interface ISummaryProps {
    data: IResponseSummarySites

    onUpdateTenant(value: Partial<IUser["setting"]>): void
}

const LiceThreshold: React.FC<ISummaryProps> = ({ data, onUpdateTenant }) => {
    const { lice_sites, lice_units } = data;
    const set = setSiteOverview();
    const setting = useSelector(selectOneSetting(SiteOverviewSetting.SUMMARY_LICE_THRESHOLD));

    const onChange = useCallback((checked: boolean) => {
        const ids = new Set(lice_units);
        if (checked) {
            set(({ unitsFilter: prev }) => ({ unitsFilter: { ...prev, [FILTER_ID.LICE]: ({ id }) => ids.has(id || "") } }))
        } else {
            set(({ unitsFilter: prev }) => ({ unitsFilter: removeProperty(prev, FILTER_ID.LICE) }))
        }
    }, [lice_units, set]);

    useEffect(() => {
        set(({ unitsFilter: prev }) => {
            if (prev[FILTER_ID.LICE]) {
                const ids = new Set(lice_units);
                return { unitsFilter: { ...prev, [FILTER_ID.LICE]: ({ id }) => ids.has(id || "") } };
            }
            return prev
        })
    }, [lice_units, set])

    return <div className={styles.section}>
        <Checkbox onChange={({ target }) => onChange(target.checked)} disabled={!lice_sites.length}>
            <div className={styles.headerSection}>
                Above lice threshold
            </div>
        </Checkbox>
        <div className={styles.bodySection}>
            {setting !== undefined && <div className="text-[20px]">
                {lice_sites.length} Sites <span className="text-comment">({lice_units.length} units)</span>
            </div>}
            <SummarySetting propertyName={SiteOverviewSetting.SUMMARY_LICE_THRESHOLD} onSave={onUpdateTenant} placeholder="Adult female lice threshold" suffix="lice/fish">
                {setting !== undefined ? `Threshold: ${setting} Adult female lice/ fish` : "Define adult female lice threshold"}
            </SummarySetting>
        </div>
    </div>
}

const Disease: React.FC<ISummaryProps> = ({ data }) => {
    const { disease_map, local_disease_sites, local_disease_units } = data;
    const set = setSiteOverview();

    const onChange = useCallback((checked: boolean) => {
        const ids = new Set(local_disease_units);
        if (checked) {
            set(({ unitsFilter: prev }) => ({ unitsFilter: { ...prev, [FILTER_ID.DISEASE]: ({ id }) => ids.has(id || "") } }))
        } else {
            set(({ unitsFilter: prev }) => ({ unitsFilter: removeProperty(prev, FILTER_ID.DISEASE) }))
        }
    }, [local_disease_units, set])

    return <div className={styles.section}>
        <div className={styles.headerSection}>
            Disease
        </div>
        <div className={styles.bodySection}>
            <div className="flex gap-5">
                {Object.keys(diseaseType).map((key) => {
                    const disease = diseaseType[key];
                    return disease_map[disease.id]?.length ? <div className={styles.diseaseIcon} style={{ backgroundColor: disease.color, color: disease.fontColor }} key={disease.id}>
                        {disease_map[disease.id]?.length || 0} {disease.name}
                    </div> : null
                })}
            </div>
            <div className="relative w-[50px]">
                <Divider className="mb-1 border-[var(--colorMain)]" />
            </div>
            <div className="text-[15px]">
                Defined disease
            </div>
            {local_disease_sites.length ? <Checkbox onChange={({ target }) => onChange(target.checked)} disabled={!local_disease_units.length}>
                <div className="text-[20px] flex items-center">
                    <Icon icon={ICON_NAME.DISEASE} size="large" className="mr-1" /> {local_disease_sites.length} Sites <span className="text-comment ml-[5px]">({local_disease_units.length} units)</span>
                </div>
            </Checkbox> : <div className="text-comment italic text-[14px]">No sites, units have diseased fish</div>}
        </div>
    </div>
}


const Density: React.FC<ISummaryProps> = ({ data, onUpdateTenant }) => {
    const { overload_sites, overload_units } = data;
    const set = setSiteOverview();
    const { [SiteOverviewSetting.SUMMARY_SITE_LIMIT_PERCENT]: siteSetting, [SiteOverviewSetting.SUMMARY_UNIT_DENSITY]: unitSetting } = useSelector(selectSetting([SiteOverviewSetting.SUMMARY_SITE_LIMIT_PERCENT, SiteOverviewSetting.SUMMARY_UNIT_DENSITY]));

    const onSiteChange = useCallback((checked: boolean) => {
        const ids = new Set(overload_sites);
        if (checked) {
            set(({ unitsFilter: prev }) => ({ unitsFilter: { ...prev, [FILTER_ID.OVERLOAD_SITE]: ({ site_id }) => ids.has(site_id || "") } }))
        } else {
            set(({ unitsFilter: prev }) => ({ unitsFilter: removeProperty(prev, FILTER_ID.OVERLOAD_SITE) }))
        }
    }, [overload_sites, set])

    const onUnitChange = useCallback((checked: boolean) => {
        const ids = new Set(overload_units);
        if (checked) {
            set(({ unitsFilter: prev }) => ({ unitsFilter: { ...prev, [FILTER_ID.OVERLOAD_UNIT]: ({ id }) => ids.has(id || "") } }))
        } else {
            set(({ unitsFilter: prev }) => ({ unitsFilter: removeProperty(prev, FILTER_ID.OVERLOAD_UNIT) }))
        }
    }, [overload_units, set])

    useEffect(() => {
        set(({ unitsFilter: prev }) => {
            if (prev[FILTER_ID.OVERLOAD_SITE]) {
                const ids = new Set(overload_sites);
                return { unitsFilter: { ...prev, [FILTER_ID.OVERLOAD_SITE]: ({ id }) => ids.has(id || "") } };
            }
            return prev
        })
    }, [overload_sites, set])

    useEffect(() => {
        set(({ unitsFilter: prev }) => {
            if (prev[FILTER_ID.OVERLOAD_UNIT]) {
                const ids = new Set(overload_units);
                return { unitsFilter: { ...prev, [FILTER_ID.OVERLOAD_UNIT]: ({ id }) => ids.has(id || "") } };
            }
            return prev
        })
    }, [overload_units, set])

    return <div className={styles.section}>
        <div className={styles.headerSection}>
            Near MAB sites | High density units
        </div>
        <div className={styles.bodySection}>
            <div className="flex">
                <div className={`flex-1 ${siteSetting !== undefined ? "text-right" : "text-center"}`}>
                    {siteSetting !== undefined && <Checkbox onChange={({ target }) => onSiteChange(target.checked)} disabled={!overload_sites.length}>
                        <div className="text-[20px]">
                            {overload_sites.length} sites
                        </div>
                    </Checkbox>}
                    <div className="pr-[8px]">
                        <SummarySetting forward={value => value * 100} reverse={value => value / 100} placeholder="Near max allowance capacity" propertyName={SiteOverviewSetting.SUMMARY_SITE_LIMIT_PERCENT} suffix="%" onSave={onUpdateTenant}>
                            {siteSetting !== undefined ? `${siteSetting * 100}% site’s capacity` : "Set the site’s Maximum Allowed Biomass threshold (%)"}
                        </SummarySetting>
                    </div>
                </div>
                <Divider orientation="vertical" className="h-[50px] border-[var(--colorMain)]" />
                <div className={`flex-1 ${siteSetting !== undefined ? "text-right" : "text-center"}`}>
                    {siteSetting !== undefined && <Checkbox onChange={({ target }) => onUnitChange(target.checked)} disabled={!overload_units.length}>
                        <div className="text-[20px]">
                            {overload_units.length} units
                        </div>
                    </Checkbox>}
                    <div className="pr-[8px]">
                        <SummarySetting forward={value => value * 1000} reverse={value => value / 1000} placeholder="High density units" propertyName={SiteOverviewSetting.SUMMARY_UNIT_DENSITY} suffix="kg/m³" onSave={onUpdateTenant}>
                            {unitSetting !== undefined ? `Threshold: ${unitSetting * 1000}kg/m³` : "Define the unit density threshold"}
                        </SummarySetting>
                    </div>
                </div>
            </div>
        </div>
    </div>
}

const HarvestSize: React.FC<ISummaryProps> = ({ data, onUpdateTenant }) => {
    const { harvest_sites, harvest_units } = data;
    const set = setSiteOverview();
    const setting = useSelector(selectOneSetting(SiteOverviewSetting.SUMMARY_HARVEST_AVG));

    const onChange = useCallback((checked: boolean) => {
        const ids = new Set(harvest_units);
        if (checked) {
            set(({ unitsFilter: prev }) => ({ unitsFilter: { ...prev, [FILTER_ID.HARVEST_SIZE]: ({ id }) => ids.has(id || "") } }))
        } else {
            set(({ unitsFilter: prev }) => ({ unitsFilter: removeProperty(prev, FILTER_ID.HARVEST_SIZE) }))
        }
    }, [harvest_units, set])

    useEffect(() => {
        set(({ unitsFilter: prev }) => {
            if (prev[FILTER_ID.HARVEST_SIZE]) {
                const ids = new Set(harvest_units);
                return { unitsFilter: { ...prev, [FILTER_ID.HARVEST_SIZE]: ({ id }) => ids.has(id || "") } };
            }
            return prev
        })
    }, [harvest_units, set])

    return <div className={styles.section}>
        <Checkbox onChange={({ target }) => onChange(target.checked)} disabled={!harvest_units.length}>
            <div className={styles.headerSection}>
                Harvest-size fish
            </div>
        </Checkbox>
        <div className={styles.bodySection}>
            {setting !== undefined && <>
                <div className="text-[20px]">
                    {harvest_sites.length} Sites <span className="text-comment">({harvest_units.length} units)</span>
                </div>
                <div className="relative w-[50px]">
                    <Divider className="mt-1 mb-0 border-[var(--colorMain)]" />
                </div>
                <div className="text-[24px] font-bold">
                    ∑ {formatNumber(Math.round(data.harvest_capacity / 1000) / 1000)} tn
                </div>
            </>}
            <SummarySetting forward={value => value / 1000} reverse={value => value * 1000} placeholder="Havest-size" propertyName={SiteOverviewSetting.SUMMARY_HARVEST_AVG} suffix="kg" onSave={onUpdateTenant}>
                {setting !== undefined ? `Harvest-size: ${formatNumber(setting / 1000)}kg` : "Set harvest-size standard"}
            </SummarySetting>
        </div>
    </div>
}

const Mortality: React.FC<ISummaryProps> = ({ data, onUpdateTenant }) => {
    const { mortality_sites, mortality_units } = data;
    const set = setSiteOverview();
    const setting = useSelector(selectOneSetting(SiteOverviewSetting.SUMMARY_MORTALITY_THRESHOLD));

    const onChange = useCallback((checked: boolean) => {
        const ids = new Set(mortality_units);
        if (checked) {
            set(({ unitsFilter: prev }) => ({ unitsFilter: { ...prev, [FILTER_ID.MORTALITY]: ({ id }) => ids.has(id || "") } }))
        } else {
            set(({ unitsFilter: prev }) => ({ unitsFilter: removeProperty(prev, FILTER_ID.MORTALITY) }))
        }
    }, [mortality_units, set])

    useEffect(() => {
        set(({ unitsFilter: prev }) => {
            if (prev[FILTER_ID.MORTALITY]) {
                const ids = new Set(mortality_units);
                return { unitsFilter: { ...prev, [FILTER_ID.MORTALITY]: ({ id }) => ids.has(id || "") } };
            }
            return prev
        })
    }, [mortality_units, set])

    return <div className={styles.section}>
        <Checkbox onChange={({ target }) => onChange(target.checked)} disabled={!mortality_units.length}>
            <div className={styles.headerSection}>
                High mortality
            </div>
        </Checkbox>
        <div className={styles.bodySection}>
            {setting !== undefined && <div className="text-[20px]">
                {mortality_sites.length} Sites <span className="text-comment">({mortality_units.length} units)</span>
            </div>}
            <SummarySetting propertyName={SiteOverviewSetting.SUMMARY_MORTALITY_THRESHOLD} onSave={onUpdateTenant} forward={value => value * 100} reverse={value => value / 100} placeholder="Unit's mortality threshold" suffix="%">
                {setting !== undefined ? `Threshold: ${setting * 100}%` : "Define high mortality threshold (%)"}
            </SummarySetting>
        </div>
    </div>
}

const StarvingUnits: React.FC<ISummaryProps> = ({ data }) => {
    const { starving_units, sites, units } = data;
    const locations = Object.keys(starving_units).map(siteId => ({
        name: sites[siteId],
        codes: starving_units[siteId].map(unitId => units[unitId])
    }))

    return <div className={styles.section}>
        <div className={styles.headerSection}>
            Today starving units
        </div>
        <div className={styles.bodySection}>
            <div className="text-center pr-1">
                {locations.map((loc, i) => (
                    <span key={loc.name}>
                        {!!i && <span className="mr-1">,</span>}
                        <span className="font-[600] mr-1">{loc.name}</span>
                        (<span>{loc.codes.join(", ")}</span>)
                    </span>
                ))}
                {!locations.length && <LoadingBox loading={false} message="No starving units" />}
            </div>
        </div>
    </div >
}

const Summary: React.FC = () => {
    const [open, setOpen] = useState(false);
    const [data, setData] = useState<IResponseSummarySites | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [isClosed] = useSiteOverview(state => state.isClosed);
    const dispatch = useDispatch();

    const onLoad = useCallback(async () => {
        setLoading(true);
        try {
            const result = await SiteService.summary();
            setLoading(false);
            setData(result);
        } catch (error) {
            setLoading(false);
        }
    }, [])

    useEffect(() => {
        onLoad();
    }, [onLoad])

    const onUpdateTenant = useCallback((value: Partial<IUser["setting"]>) => {
        dispatch(updateUserSetting([value]));
        onLoad();
    }, [dispatch, onLoad])

    if (isClosed)
        return null;

    return <>
        <div className={styles.btSummary} onClick={() => setOpen(true)} >
            Summary
        </div>
        <div className={styles.summary} data-open={open}>
            <div className="pt-4 text-[24px] pl-[20px] sticky top-0 bg-[var(--bgHeader)] z-10 flex justify-between items-center pr-2">
                <div>Today - <span className="text-comment">{datetime().format('MMMM DD, YYYY')}</span></div>
                <div className={styles.btClose}>
                    <div className='bt-close' onClick={() => setOpen(false)} />
                </div>
            </div>
            <div className="italic text-comment mb-3 pl-[20px]">
                Define your criteria to get latest statuses  for all sites, units
            </div>
            <div className="relative">
                {data
                    ? <>
                        <LiceThreshold data={data} onUpdateTenant={onUpdateTenant} />
                        <Disease data={data} onUpdateTenant={onUpdateTenant} />
                        <FishHealth data={data} />
                        <Density data={data} onUpdateTenant={onUpdateTenant} />
                        <HarvestSize data={data} onUpdateTenant={onUpdateTenant} />
                        <Mortality data={data} onUpdateTenant={onUpdateTenant} />
                        <SiteUtilize data={data} />
                        <StarvingUnits data={data} onUpdateTenant={onUpdateTenant} />
                    </>
                    : <LoadingBox className={`mt-2 ${data ? "" : "max-h-[300px]"}`} loading={loading} level={data ? "bg" : "box"} />}
            </div>
        </div>
    </>
}

export default Summary;